import { useState } from "react";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { api, getApiError } from "@/lib/api";
import { formatINR, toLocalInputValue } from "@/lib/format";
import { shareTxnOnWhatsApp } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const EMPTY = { type: "credit", amount: "", note: "", date: "" };

const TypeButton = ({ value, current, onSelect }) => {
  const active = value === current;
  const isCredit = value === "credit";
  const Icon = isCredit ? ArrowDownLeft : ArrowUpRight;
  const activeCls = isCredit ? "bg-emerald-700 hover:bg-emerald-800" : "bg-red-700 hover:bg-red-800";
  return (
    <Button
      type="button"
      data-testid={`txn-type-${value}`}
      variant={active ? "default" : "outline"}
      onClick={() => onSelect(value)}
      className={active ? `${activeCls} text-white gap-1` : "gap-1"}
    >
      <Icon className="h-4 w-4" /> {isCredit ? "Credit" : "Debit"}
    </Button>
  );
};

export const RecordTxnForm = ({ customer, onRecorded }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(form.amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/customers/${customer.id}/transactions`, {
        type: form.type,
        amount: amt,
        note: form.note,
        ...(form.date ? { created_at: new Date(form.date).toISOString() } : {}),
      });
      const verb = form.type === "credit" ? "Credited" : "Debited";
      toast.success(`${verb} ${formatINR(amt)}. New balance ${formatINR(data.new_balance)}`, {
        duration: 15000,
        action: { label: "Send on WhatsApp", onClick: () => shareTxnOnWhatsApp(customer, data.transaction) },
      });
      setForm((f) => ({ ...EMPTY, type: f.type }));
      onRecorded();
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-4 items-end" data-testid="txn-form">
      <div className="md:col-span-3 space-y-2">
        <Label className="label-overline">Type</Label>
        <div className="grid grid-cols-2 gap-2">
          <TypeButton value="credit" current={form.type} onSelect={set("type")} />
          <TypeButton value="debit" current={form.type} onSelect={set("type")} />
        </div>
      </div>
      <div className="md:col-span-2 space-y-2">
        <Label className="label-overline">Amount (₹)</Label>
        <Input data-testid="txn-amount-input" type="number" min="0.01" step="0.01" required placeholder="0.00"
          value={form.amount} onChange={(e) => set("amount")(e.target.value)} className="font-mono-num" />
      </div>
      <div className="md:col-span-3 space-y-2">
        <Label className="label-overline">Date &amp; time (optional)</Label>
        <Input data-testid="txn-date-input" type="datetime-local" max={toLocalInputValue()}
          value={form.date} onChange={(e) => set("date")(e.target.value)} className="font-mono-num" />
      </div>
      <div className="md:col-span-2 space-y-2">
        <Label className="label-overline">Note</Label>
        <Input data-testid="txn-note-input" placeholder="Optional" maxLength={200}
          value={form.note} onChange={(e) => set("note")(e.target.value)} />
      </div>
      <div className="md:col-span-2">
        <Button type="submit" disabled={submitting} data-testid="submit-txn-btn" className="w-full bg-slate-900 hover:bg-slate-800">
          {submitting ? "Saving…" : "Record"}
        </Button>
      </div>
    </form>
  );
};
